import { useEntity } from '../../context/EntityContext';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import React, { lazy, Suspense } from 'react';

// Lazy load specific dashboards
const FoundationDashboard = lazy(() => import('../foundation/FoundationDashboard').then(m => ({ default: m.FoundationDashboard })));

// Placeholders for now - we will build these next
const ForProfitDashboard = lazy(() => import('./ForProfitDashboard').then(m => ({ default: m.ForProfitDashboard })));
const NonprofitDashboard = lazy(() => import('./NonprofitDashboard').then(m => ({ default: m.NonprofitDashboard })));
const TrustDashboard = lazy(() => import('./TrustDashboard').then(m => ({ default: m.TrustDashboard })));
const EstateDashboard = lazy(() => import('./EstateDashboard').then(m => ({ default: m.EstateDashboard })));

export function DashboardRouter() {
    const { currentEntity, isLoading } = useEntity();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-accent-500" />
            </div>
        );
    }

    if (!currentEntity) {
        // If no entity exists, prompt creation? Or show an empty state.
        // For now, redirect to intake if absolutely no entity, 
        // but in reality we probably just show a "Select or Create Entity" screen.
        return <Navigate to="/intake/new-entity" />;
    }

    const { type } = currentEntity;

    // Route based on entity category/type
    switch (type) {
        case 'PRIVATE_FOUNDATION':
        case 'PRIVATE_OPERATING_FOUNDATION':
        case 'SUPPORTING_ORGANIZATION':
            return (
                <Suspense fallback={<DashboardLoader />}>
                    <FoundationDashboard />
                </Suspense>
            );

        case 'PUBLIC_CHARITY':
        case '501C4':
        case '501C6':
        case '501C7':
        case 'HOA':
            return (
                <Suspense fallback={<DashboardLoader />}>
                    <NonprofitDashboard />
                </Suspense>
            );

        case 'REVOCABLE_TRUST':
        case 'IRREVOCABLE_TRUST':
        case 'CHARITABLE_TRUST':
        case 'SPECIAL_NEEDS_TRUST':
            return (
                <Suspense fallback={<DashboardLoader />}>
                    <TrustDashboard />
                </Suspense>
            );

        case 'ESTATE':
            return (
                <Suspense fallback={<DashboardLoader />}>
                    <EstateDashboard />
                </Suspense>
            );

        case 'INDIVIDUAL':
            // Individual might share for-profit style or have a personal dashboard
            // using ForProfit for now or creating a specific one.
            return (
                <Suspense fallback={<DashboardLoader />}>
                    <ForProfitDashboard />
                </Suspense>
            );

        default:
            // All For-Profit types (LLC, C-Corp, S-Corp, Partnership, etc.)
            return (
                <Suspense fallback={<DashboardLoader />}>
                    <ForProfitDashboard />
                </Suspense>
            );
    }
}

function DashboardLoader() {
    return (
        <div className="flex items-center justify-center h-[50vh]">
            <Loader2 className="w-8 h-8 animate-spin text-accent-500" />
        </div>
    );
}
